import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Settings, Link2, Loader2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import UserRoleDisplay from "@/lib/auth/UserRoleDisplay";
import { UserAvatar } from "@/components/ui/user-avatar";

interface UserAvatarMenuProps {
  displayName: string;
  email: string;
  /** Foto profilo, se caricata: altrimenti il pallino ripiega sulle iniziali. */
  avatarUrl?: string | null;
  onLogout: () => void;
  isLoggingOut?: boolean;
  /** Copia negli appunti il link pubblico di richiesta viaggio. */
  onCopyTripRequestLink?: () => void;
  isCopyingTripRequestLink?: boolean;
}

export const UserAvatarMenu = ({
  displayName,
  email,
  avatarUrl,
  onLogout,
  isLoggingOut = false,
  onCopyTripRequestLink,
  isCopyingTripRequestLink = false,
}: UserAvatarMenuProps) => {
  const { t } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative p-0 h-10 w-10 md:h-8 md:w-8 rounded-full hover:bg-transparent"
          aria-label={displayName}
        >
          {/* Stesso pallino colorato che gli altri utenti vedono di noi: in testata era un
              generico omino grigio, unica occorrenza fuori standard. */}
          <UserAvatar
            fullName={displayName}
            email={email}
            imageUrl={avatarUrl}
            className="h-10 w-10 md:h-8 md:w-8 text-sm"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end" forceMount>
        <div className="flex flex-col gap-2 p-2">
          <div className="flex items-center gap-3">
            <div className="flex flex-col leading-none min-w-0 flex-1">
              <p className="font-medium truncate">{displayName}</p>
              <p className="text-sm text-muted-foreground truncate">{email}</p>
            </div>
          </div>
          <div className="flex justify-start">
            <UserRoleDisplay />
          </div>
        </div>
        <DropdownMenuSeparator />
        {/* Il link di richiesta viaggio vale ovunque e non è l'azione di una
            pagina: sta qui, non accanto alle notifiche. */}
        {onCopyTripRequestLink && (
          <DropdownMenuItem
            className="cursor-pointer"
            disabled={isCopyingTripRequestLink}
            onSelect={(event) => {
              event.preventDefault();
              onCopyTripRequestLink();
            }}
          >
            {isCopyingTripRequestLink
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Link2 className="mr-2 h-4 w-4" />}
            <span className="text-sm">{t("navigation.shareTripRequestLink")}</span>
          </DropdownMenuItem>
        )}
        {/* <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span className="text-sm">{t("navigation.profile")}</span>
          </Link>
        </DropdownMenuItem> */}
        <DropdownMenuItem asChild>
          <Link to="/profile?tab=account-settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            <span className="text-sm">{t("settings.accountSettings")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={onLogout}
          disabled={isLoggingOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span className="text-sm">
            {isLoggingOut ? t("auth.loggingOut") : t("auth.logout")}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
